import { AfterViewInit, Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { FileVaultCategory, FileVaultReferenceRelation, FileVaultSubjectType, MarketingFileReference as MFR } from 'situview-webapi-client';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { FileVaultService } from 'src/app/services/file-vault.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { FileType } from '../marketing.component';

export const imageTypes = ['image/jpg', 'image/jpeg', 'image/webp', 'image/svg', 'image/png', 'image/gif', 'image/avif', 'image/apng'];

interface MarketingFileReference extends MFR {
  map?: MarketingFileReference;
}

export interface FileMapEditorDialogData {
  file: MarketingFileReference;
  subjectId: string;
  subjectType: FileVaultSubjectType;
  isPreviewOnly?: boolean;
  resources: MarketingFileReference[];
}

export interface FileMapEditorDialogResult {
  file?: string;
  action: 'delete' | 'save'
}

export interface FileMap {
  width: number;
  height: number;
  interactions: (ZoneRect | ZoneCircle)[];
}

export interface OpenFileAction {
  targetType?: 'FileVault' | 'ShowcaseRef'
  target?: MarketingFileReference;
  targetId?: string,
}

export type Action = OpenFileAction;

export interface Zone extends Action {
  shape: 'Rect' | 'Circle';
  x: number;
  y: number;
  selected: boolean;
}

export interface ZoneRect extends Zone {
  shape: 'Rect';
  width: number;
  height: number;
}

export interface ZoneCircle extends Zone {
  shape: 'Circle';
  radius: number;
}

@Component({
  selector: 'app-marketing',
  templateUrl: './file-map-editor.component.html',
  styleUrls: ['./file-map-editor.component.scss']
})
export class FileMapEditorComponent extends SituBaseComponent implements OnDestroy, AfterViewInit {

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    // bail if user is typing in an input/textarea/contenteditable
    const element = document.activeElement as HTMLElement | null;
    if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable)) {
      return;
    }
    if (!this.selectedZone) return;

    const step = event.shiftKey ? 10 : 1;

    switch (event.key) {
      case 'ArrowUp':
        this.selectedZone.y -= step;
        event.preventDefault();
        break;
      case 'ArrowDown':
        this.selectedZone.y += step;
        event.preventDefault();
        break;
      case 'ArrowLeft':
        this.selectedZone.x -= step;
        event.preventDefault();
        break;
      case 'ArrowRight':
        this.selectedZone.x += step;
        event.preventDefault();
        break;
    }
  }

  public zones: (ZoneRect | ZoneCircle)[] = [];
  public selectedZone?: Zone;
  public fileMap: FileMap = {} as FileMap;
  public fileVaultRef?: FileVaultReferenceRelation;
  public isImage = false;
  public isVideo = false;
  public handleScale = 1;
  public FileType = FileType;
  public safeUrl;

  public dragging: SVGGraphicsElement | null = null;
  public zoneInitial?: ZoneRect | ZoneCircle = undefined;
  public pointerStart = { x: 0, y: 0 };
  public dragTracking?: (event: PointerEvent, pos?: 'TL' | 'TR' | 'BL' | 'BR') => void = undefined;
  public scheduled = false;

  private initialState: string = JSON.stringify(this.zones);

  constructor(
    readonly authService: AuthService,
    readonly stateService: StateService,
    private readonly dialogRef: MatDialogRef<FileMapEditorComponent, FileMapEditorDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: FileMapEditorDialogData,
    private readonly snackBar: MatSnackBar,
    private readonly fileVaultService: FileVaultService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly sanitiser: DomSanitizer,
  ) {
    super(authService, stateService);

    this.dialogRef.disableClose = true;

    this.isImage = imageTypes.includes(data.file.fileContentType || '');
    this.isVideo = data.file.fileContentType === 'video/mp4';

    if (data.file?.fileContentType === 'application/pdf' && data.file.uri) {
      this.safeUrl = this.sanitiser.bypassSecurityTrustResourceUrl(data.file.uri);
    }

    if (data?.file && (this.isImage || this.isVideo)) {
      const map = data.file.map;
      this.fileVaultRef = map;
      if (map && map.uri) {
        fetch(map.uri).then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        }).then(data => {
          this.fileMap = data;
          //timeout to fix angular cdr race conditions
          this.zones = [...this.fileMap.interactions];
          this.zones.forEach(z => {
            if (z.targetId) z.target = this.data.resources.find(f => f.id === z.targetId);
          });

          this.initialState = JSON.stringify(this.zones);
        })
          .catch(err => {
            this.snackBar.open('Failed to load file map:', 'OK', { duration: 2000 });
          });
      } else if (this.isImage) {
        const img = new Image();
        img.src = data.file.uri || '';

        img.onload = () => {
          this.fileMap.height = img.height;
          this.fileMap.width = img.width;
        };
      } else if (this.isVideo) {
        const video = document.createElement('video');
        video.src = data.file.uri || '';

        video.addEventListener('loadedmetadata', () => {
          this.fileMap.height = video.videoHeight;
          this.fileMap.width = video.videoWidth
        });
      }
    }
  }
  ngAfterViewInit(): void {
    this.dialogRef.backdropClick().subscribe(() => this.tryClose());
    this.dialogRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') this.tryClose();
    });
  }

  public tryClose(): void {
    if (JSON.stringify(this.zones) === this.initialState) this.dialogRef.close();
  }

  public setScale(event: any): void {

    if (event.target.tagName === "IMG") {
      this.handleScale = event.target.naturalWidth / event.target.clientWidth;
    } else if (event.target.tagName === "VIDEO") {
      this.handleScale = event.target.videoWidth / event.target.clientWidth;
    }
  }

  public onMovePointerDown(event: PointerEvent, zone: ZoneRect | ZoneCircle) {
    const target = event.target as SVGGraphicsElement;
    if (!target) return;

    const svg = target.ownerSVGElement!;
    const pt = this.toSvgPoint(svg, event);
    this.zoneInitial = {...zone};
    this.pointerStart.x = pt.x;
    this.pointerStart.y = pt.y;
    this.dragging = target;

    this.onZoneSelected(zone);
    target.setPointerCapture(event.pointerId);
    this.dragTracking = this.onMovePointerMove.bind(this)
    svg.addEventListener('pointermove', this.dragTracking);
    svg.classList.add("dragging");
  }

  public onMovePointerUp(event: PointerEvent) {
    if (!this.dragging) return;

    const svg = this.dragging.ownerSVGElement!;
    if (this.dragTracking) {
      svg.removeEventListener('pointermove', this.dragTracking);
      this.dragTracking = undefined;
    }
    svg.classList.remove("dragging");
    this.dragging.releasePointerCapture(event.pointerId);
    this.dragging = null;
  }

  public onMovePointerMove(event: PointerEvent) {
    if (!this.dragging || this.scheduled) return;

    const svg = this.dragging.ownerSVGElement!;
    const pt = this.toSvgPoint(svg, event);

    requestAnimationFrame(() => {
      this.scheduled = false;
      if (!this.dragging) {
        return;
      }
      if (this.selectedZone) {
        const x = Math.round(this.zoneInitial!.x - (this.pointerStart.x - pt.x));
        const y = Math.round(this.zoneInitial!.y - (this.pointerStart.y - pt.y));

        if (this.selectedZone.shape === 'Circle') {
          const rad = (this.selectedZone as ZoneCircle).radius;
          this.selectedZone.x = Math.min(this.fileMap.width + rad - 5, Math.max(5 - rad, x));
          this.selectedZone.y = Math.min(this.fileMap.height + rad - 5, Math.max(5 - rad, y));
        } else {
          this.selectedZone.x = Math.min(this.fileMap.width - 5, Math.max(5 - (this.selectedZone as ZoneRect).width, x));
          this.selectedZone.y = Math.min(this.fileMap.height - 5, Math.max(5 - (this.selectedZone as ZoneRect).height, y));
        }
      }
    });
  }

  public onResizePointerDown(event: PointerEvent, zone: ZoneRect | ZoneCircle, pos: 'TL' | 'TR' | 'BL' | 'BR') {
    const target = event.target as SVGGraphicsElement;
    const svg = target.ownerSVGElement!;
    const pt = this.toSvgPoint(svg, event);
    this.pointerStart.x = pt.x;
    this.pointerStart.y = pt.y;
    this.zoneInitial = {...zone};
    this.dragging = target;

    target.setPointerCapture(event.pointerId);

    this.dragTracking = (e: PointerEvent) => this.onResizePointerMove(e, zone, pos);
    svg.addEventListener('pointermove', this.dragTracking);
  }

  public onResizePointerUp(event: PointerEvent) {
    if (!this.dragging) return;
    const svg = this.dragging.ownerSVGElement!;

    this.dragging.releasePointerCapture(event.pointerId);

    if (this.dragTracking) {
      svg.removeEventListener('pointermove', this.dragTracking);
      this.dragTracking = undefined;
    }

    this.dragging = null;
  }

  private onResizePointerMove(event: PointerEvent, zone: Zone, pos: 'TL' | 'TR' | 'BL' | 'BR') {
    if (!this.dragging || this.scheduled) return;
    const svg = (event.target as SVGElement).ownerSVGElement!;
    const pt = this.toSvgPoint(svg, event);

    // cumulative diff
    const dx = pt.x - this.pointerStart.x;
    const dy = pt.y - this.pointerStart.y;

    requestAnimationFrame(() => {
       this.scheduled = false;
      if (!this.dragging) {
        return;
      }

      if (zone.shape === 'Rect') {
        if (pos === 'TL' || pos === 'BL') {
          zone.x = Math.round(Math.min(this.fileMap.width - 5, Math.max(0, this.zoneInitial!.x + dx)));
          (zone as ZoneRect).width = Math.round(Math.max(5, (this.zoneInitial as ZoneRect).width - dx));
        } else if (pos === 'TR' || 'BR') {
          (zone as ZoneRect).width = Math.round(Math.max(5, (this.zoneInitial as ZoneRect).width + dx));
        }

        if (pos === 'TL' || pos == 'TR') {
          zone.y = Math.round(Math.min(this.fileMap.height - 5, Math.max(0, this.zoneInitial!.y + dy)));
          (zone as ZoneRect).height = Math.round(Math.max(5, (this.zoneInitial as ZoneRect).height - dy));
        } else if (pos === 'BL' || 'BR') {
          (zone as ZoneRect).height = Math.round(Math.max(5, (this.zoneInitial as ZoneRect).height + dy));
        }
      }

      else if (zone.shape === 'Circle') {
        const hx = (pos === 'TR' || pos === 'BR') ? 1 : -1;
        const hy = (pos === 'BL' || pos === 'BR') ? 1 : -1;

        const len = Math.hypot(hx, hy);
        const ux = hx / len;
        const uy = hy / len;
        const delta = dx * ux + dy * uy / 2;

        (zone as ZoneCircle).radius = Math.round(Math.max(5, (this.zoneInitial as ZoneCircle).radius + delta));
      }
    });
  }

  public toSvgPoint(svg: SVGSVGElement, event: PointerEvent) {
    const pt = new DOMPoint(event.clientX, event.clientY);
    return pt.matrixTransform(svg.getScreenCTM()!.inverse());
  }

  public onSwapShape(zone: Zone): void {
    switch (zone.shape) {
      case 'Rect': {
        zone.shape = 'Circle';
        const rad = Math.floor((zone as ZoneRect).width / 2);
        (zone as ZoneCircle).radius = rad;
        zone.x += rad;
        zone.y += rad;
        delete (zone as Partial<ZoneRect>).height;
        delete (zone as Partial<ZoneRect>).width;
        break;
      }
      case 'Circle': {
        zone.shape = 'Rect';
        const rad = (zone as ZoneCircle).radius;
        (zone as ZoneRect).width = rad * 2;
        (zone as ZoneRect).height = rad * 2;
        zone.x -= rad;
        zone.y -= rad;
        delete (zone as Partial<ZoneCircle>).radius;
        break;
      }
    }
  }

  public onZoneSelected(zone: Zone): void {
    if (this.selectedZone) {
      this.selectedZone.selected = false;
    } else {
      this.zones.forEach(z => z.selected = false);
    }
    this.selectedZone = zone;
    zone.selected = true;
  }

  public onAddZone(): void {
    Math.floor(this.fileMap.height / 2);
    this.zones.push({
      x: Math.floor(this.fileMap.width / 4),
      y: Math.floor(this.fileMap.height / 4),
      height: Math.floor(this.fileMap.height / 2),
      width: Math.floor(this.fileMap.width / 2),
      shape: 'Rect',
    } as ZoneRect);
  }

  public onRemoveZone(zone: ZoneRect | ZoneCircle): void {
    const indx = this.zones.indexOf(zone);
    this.zones.splice(indx, 1);
  }

  public async onSave(): Promise<void> {
    // remove targets from http payload
    this.fileMap.interactions = this.zones.map(z => {
      const newZ = { ...z };
      newZ.targetId = z.target?.id;
      delete newZ.target;
      return newZ;
    });
    const file = this.createJsonFile(this.fileMap, this.data.file.id! + '.map.json');
    if (this.selectedCompany?.id) {
      const res = await this.fileVaultService.uploadBlob(this.selectedCompany.id!, this.data.subjectId, this.data.subjectType, FileVaultCategory.MarketingMaterial, file!, 'map')
      if (res) {
        this.snackBar.open(`File map saved`, 'OK', { duration: 2000 });
        this.dialogRef.close({ action: 'save' });
      }
    }
  }

  public async onRemove(): Promise<void> {
    this.confirmDialogService.open({
      title: 'Remove File',
      message: `Are you sure you wish to remove file map permanently?`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    const confirmed = await this.confirmDialogService.confirmed().toPromise();
    if (confirmed && this.selectedCompany) {
      try {
        const res = await this.fileVaultService.removeFileRequest(this.selectedCompany.id!, this.data.subjectId, this.fileVaultRef!);
        if (res.removedCount) {
          this.dialogRef.close({ action: 'delete' });
        }
      }
      catch {
        this.snackBar.open(`Failed to remove file`, 'OK', { duration: 2000 });
      }
    }
  }

  public createJsonFile(obj: Object, fileName: string): File {
    return new File([JSON.stringify(obj)], fileName, { type: 'application/json' });
  }

  public getFileDownloadUrl(file: FileVaultReferenceRelation): string {
    return this.fileVaultService.getFileDownloadUrl(file);
  }
}